import Divider from 'components/divider';
import FoodTypeSelector from 'components/pet-profile/food-type-selector';
import TastePreferenceSelector from 'components/pet-profile/taste-preference-selector';
import { InputProductType } from 'interfaces/api-input/input-product-type';
import React, { memo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useClickAway } from 'react-use';
import { useNeedCapture } from '../../../../state';
import AnimateDropdown from '../animate-dropdown';
import SelectorButton from '../selector-button';

interface FoodTypeAndTasteSelectorProps {
  isMobile: boolean;
}

/**
 * Food Taste Selector component of the NeedCapture component
 */
const FoodTypeAndTasteSelector: React.FC<FoodTypeAndTasteSelectorProps> = ({
  isMobile,
}) => {
  const {
    draftData,
    state,
    dispatch,
    handleCloseFoodTypeAndTasteSelector,
    handleConfirmFoodTypeAndTaste,
    handleSelectFoodTypes,
    handleSelectTastePreferences,
    handleSelectWetFoodTypes,
  } = useNeedCapture();
  const { draftFoodTypes, draftWetFoodTypes, draftTastePreferences } =
    draftData;
  const foodTasteSelectorRef = useRef<HTMLDivElement>(null);

  useClickAway(foodTasteSelectorRef, () => {
    if (!isMobile && state.isFoodTypeAndTasteSelectorOpen)
      dispatch({ type: 'TOGGLE_FOOD_TYPE_AND_TASTE_SELECTOR' });
  });

  const formatSelectedOptions = (): React.JSX.Element | null => {
    const parts: Array<{ id: string; key: string }> = [];

    // Add food draftFoodTypes
    if (draftFoodTypes?.length) {
      draftFoodTypes.forEach((type) => {
        parts.push({
          id: `pet-profile:feed-type-selector:${type}`,
          key: type,
        });
      });
    }

    // Add wet food types if wet food is selected
    if (
      draftFoodTypes?.includes(InputProductType.WetFood) &&
      draftWetFoodTypes?.length
    ) {
      draftWetFoodTypes.forEach((type) => {
        parts.push({
          id: `pet-profile:feed-type-selector:wet_food:${type}`,
          key: `wet-${type}`,
        });
      });
    }

    // Add tastes
    if (draftTastePreferences?.length) {
      draftTastePreferences.forEach((taste) => {
        parts.push({
          id: `cdp:filter:sorts:${taste}`,
          key: `taste-${taste}`,
        });
      });
    }

    if (parts.length === 0) return null;

    return (
      <div className="line-clamp-1 text-left">
        {parts.map((part, index) => (
          <React.Fragment key={part.key}>
            <FormattedMessage id={part.id} />
            {index < parts.length - 2 && ', '}
            {index === parts.length - 2 && ' & '}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="flex w-full items-center gap-3">
      <span className="block shrink-0">
        <FormattedMessage
          id={`landingpage:need-capture:age:text:cat:separator-${isMobile ? '3' : '5'}`}
        />
      </span>

      <div
        className="relative flex w-full items-center lg:block"
        ref={foodTasteSelectorRef}
      >
        <SelectorButton
          isOpen={state.isFoodTypeAndTasteSelectorOpen}
          onClick={() =>
            dispatch({ type: 'TOGGLE_FOOD_TYPE_AND_TASTE_SELECTOR' })
          }
        >
          {formatSelectedOptions() ?? (
            <span className="italic">
              <FormattedMessage id="landingpage:need-capture:food-type-and-taste:placeholder" />
            </span>
          )}
        </SelectorButton>

        {/* Desktop Dropdown */}
        {!isMobile && (
          <AnimateDropdown
            className="!max-h-120 lg:min-w-142"
            isOpen={state.isFoodTypeAndTasteSelectorOpen}
            hasButtons
            onBack={() => handleCloseFoodTypeAndTasteSelector()}
            onConfirm={() => handleConfirmFoodTypeAndTaste()}
          >
            <FoodTypeSelector
              className="mx-4"
              selectedOption={draftData}
              onSelectFoodTypes={handleSelectFoodTypes}
              onSelectWetFoodTypes={handleSelectWetFoodTypes}
            />
            <Divider />
            <TastePreferenceSelector
              className="mx-4"
              selectedOptions={draftData.draftTastePreferences}
              onSelectTastePreference={handleSelectTastePreferences}
            />
          </AnimateDropdown>
        )}
      </div>
    </div>
  );
};

export default memo(FoodTypeAndTasteSelector);
